import React, { useEffect } from 'react';
import {
   useRecoilRefresher_UNSTABLE,
   useRecoilState,
   useRecoilValue,
} from 'recoil';
import api from '../../lib/api';
import {
   ICustomer,
   ICustomerRecent,
   IEmployee,
   ILoginDetails,
   IUserInvite,
} from '../../lib/types';
import { affiliateAtom } from '../../recoil/affiliate';
import { customersArchiveAtom } from '../../recoil/customer-archive';
import {
   customerRecentSelector,
   reloadRecentCustomers,
} from '../../recoil/customer-recent';
import {
   customersSearchQueryAtom,
   searchedCustomersSelector,
} from '../../recoil/customer-search';
import { customersAtom } from '../../recoil/customers';
import { filteredSortedCustomersSelector } from '../../recoil/customers-sort-filter';
import CardCustomer from '../card-customer/card-customer';
import CheckboxText from '../checkbox-text/checkbox-text';
import InviteButton from '../invite-button/invite-button';
import Search from '../search/search';
import SortFilter from '../sort-filter/sort-filter';
import './affiliate-attorney.scss';
import EmployeeSort from '../employee-sort-filter/employee-sort-filter';
import {
   employeesSearchQueryAtom,
   searchedEmployeesSelector,
} from '../../recoil/employee-search';
import { filteredEmployeeSelector } from '../../recoil/employee-sort-filter';
import { loadSelector } from '../../recoil/load';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import CardEmployee from '../card-employee/card-employee';
import GeneralSearch from '../general-search/general-search';
import CustomerPaging from '../customer-paging/customer-paging';
import EmployeePaging from '../employee-paging/employee-paging';
import { customerPagingAtom } from '../../recoil/customer-paging';
import { employeePagingAtom } from '../../recoil/employee-paging';

export default function AffiliateAttorney(): JSX.Element {
   const navigate = useNavigate();
   const clientCurrentPage: number = useRecoilValue(customerPagingAtom);
   const employeeCurrentPage: number = useRecoilValue(employeePagingAtom);
   const [activeTab, setActiveTab] = React.useState<'Employees' | 'Clients'>(
      'Clients',
   );
   const employeeListRefresh = useRecoilRefresher_UNSTABLE(
      filteredEmployeeSelector,
   );

   const [searchQuery, setSearchQuery] = useRecoilState(
      employeesSearchQueryAtom,
   );
   const searchResults = useRecoilValue(searchedEmployeesSelector);
   const load = useRecoilValue(loadSelector);

   const filteredSortedList: ICustomer[] = useRecoilValue(
      filteredSortedCustomersSelector,
   );
   const filteredSortedEmployeeList: IEmployee[] = useRecoilValue(
      filteredEmployeeSelector,
   );
   const customers = useRecoilValue(customersAtom);
   const affiliate = useRecoilValue(affiliateAtom);

   const customerRecents: ICustomerRecent[] = useRecoilValue(
      customerRecentSelector(affiliate?.id),
   );
   const customerRecentRefresh = useRecoilRefresher_UNSTABLE(
      customerRecentSelector(affiliate?.id),
   );
   const [reloadRecent, setReloadRecent] = useRecoilState(
      reloadRecentCustomers,
   );

   const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
   };

   const handleClearSearch = (): void => {
      if (searchQuery) {
         setSearchQuery('');
      }
   };

   const employeeList =
      searchQuery.trim() && searchResults.length > 0
         ? searchResults.map((result) => result.entity) // Show search results if a query exists
         : filteredSortedEmployeeList; // Show the default employee list if no query

   useEffect(() => {
      const handleEffect = async () => {
         if (reloadRecent) {
            customerRecentRefresh();
            setReloadRecent(false);
         }

         const cookie = Cookies.get('x-affdash-actor');

         // Post login details on mount if affiliate ID is present
         if (
            affiliate?.id &&
            !sessionStorage.getItem('hasPostedLogin') &&
            !cookie
         ) {
            sessionStorage.setItem('hasPostedLogin', 'true');
            const loginDetails: ILoginDetails = {
               userId: affiliate.id,
            };

            try {
               await api.postLoginDetails(loginDetails);
            } catch (error) {
               console.error('Error posting login details:', error);
            }
         }
      };

      handleEffect();
   }, [reloadRecent, customerRecentRefresh, affiliate?.id]);

   return (
      <div className="affiliate-attorney">
         {/* Tabs */}
         <div className="tab-container">
            <div className="tab">
               <button
                  className={`tab-button ${activeTab === 'Clients' ? 'active' : ''}`}
                  onClick={() => {
                     setActiveTab('Clients');
                     handleClearSearch();
                  }}
               >
                  Clients
               </button>
               <button
                  className={`tab-button ${activeTab === 'Employees' ? 'active' : ''}`}
                  onClick={() => {
                     setActiveTab('Employees');
                     handleClearSearch();
                  }}
               >
                  Employees
               </button>
            </div>
         </div>

         {/* Content based on active tab */}
         {activeTab === 'Clients' && (
            <>
               <Search
                  placeholderPrimary={'for an Active Client '}
                  placeholderSecondary={'by name or customer ID'}
                  searchQueryAtom={customersSearchQueryAtom}
                  searchResultSelector={searchedCustomersSelector}
                  searchById={true}
                  resultPageLink={'client-details'}
                  customerRecents={customerRecents}
                  customers={customers}
                  disabled={false}
               />
               <SortFilter affiliateId={affiliate?.id} disabled={false} />

               <div className="content-wrapper">
                  <div className="attorney-content">
                     <div className="attorney-view-header">
                        <div className="btn-wrapper">
                           <InviteButton
                              inviteTarget="Client"
                              headerText="Invite a New Client"
                              bodyText="Inviting a new client will send a release for information (ROI) form to your client's email. Once they sign the ROI, they will be added to your client list."
                              confirmText="Send ROI Request"
                              successTextPre="ROI sent to "
                              successTextPost=" email. Once they have signed, they will be added to your client list and tagged with “New Client”."
                              inviteFunction={api.postInviteCustomer}
                              affiliateId={affiliate?.id}
                           />
                        </div>
                        <CheckboxText
                           text="Show archive"
                           checkboxState={customersArchiveAtom}
                        />
                     </div>

                     <div className="tab-content">
                        {filteredSortedList
                           .slice(0 + 25 * clientCurrentPage, 25 + 25 * clientCurrentPage)
                           .map((customer) => (
                              <CardCustomer
                                 key={customer.id}
                                 customer={customer}
                              />
                           ))}
                        <CustomerPaging />
                     </div>
                  </div>
               </div>
            </>
         )}

         {activeTab === 'Employees' && (
            <>
               <GeneralSearch
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="for a Employee "
                  placeholderSecondary="by name or ID"
                  clearable={true}
                  onClear={handleClearSearch}
               />

               <EmployeeSort />

               <div className="content-wrapper">
                  <div className="attorney-content">
                     <div className="btn-wrapper">
                        <InviteButton
                           inviteTarget="Employee"
                           headerText="Add New Employee"
                           bodyText="Adding a new employee will send an invitation to their email to join your network."
                           confirmText="Save"
                           successTextPre="Invitation sent to "
                           successTextPost=" email. Once they accept, they will be added to your employee list."
                           inviteFunction={(
                              invite,
                           ): Promise<IUserInvite | null> => {
                              if (affiliate?.id) {
                                 return api
                                    .postAddEmployee(invite, affiliate.id)
                                    .then((response) => {
                                       employeeListRefresh(); // ✅ Refresh employee list

                                       // Ensure response is of type IUserInvite before returning
                                       if (
                                          response &&
                                          'userEmail' in response
                                       ) {
                                          return response as IUserInvite;
                                       }
                                       return null;
                                    })
                                    .catch((error) => {
                                       console.error(
                                          'Error adding employee:',
                                          error,
                                       );
                                       return null;
                                    });
                              } else {
                                 console.error(
                                    'Affiliate ID is not available.',
                                 );
                                 return Promise.resolve(null);
                              }
                           }}
                        />
                     </div>

                     <div className="tab-content">
                        {employeeList.length > 0 ? (
                           employeeList
                           .slice(0 + 25 * employeeCurrentPage, 25 + 25 * employeeCurrentPage)
                           .map((employee) => (
                              <CardEmployee
                                 key={employee.id}
                                 employee={employee}
                              />
                           ))
                        ) : (
                           <CardEmployee employee={null} />
                        )}
                        <EmployeePaging />
                     </div>
                  </div>
               </div>
            </>
         )}
      </div>
   );
}
